import { memo, useCallback, useEffect, useRef, useState } from "react";
import { useCanvasApi } from "../../../context/CanvasContext";
interface TimerWidgetProps {
  id: string;
  data: {
    totalSeconds?: number;
    isRunning?: boolean;
    startedAt?: number | null;
    elapsedAtPause?: number;
    [key: string]: unknown;
  };
  dragging?: boolean;
}

export const TimerWidget = memo(function TimerWidget({
  id,
  data,
  dragging,
}: TimerWidgetProps) {
  const canvas = useCanvasApi();
  const { canEdit } = canvas;

  const totalSecs = Math.max(
    10,
    Math.round(parseFloat(String(data.totalSeconds ?? 120)) || 120),
  );
  const isRunning = Boolean(data.isRunning);
  const startedAt = typeof data.startedAt === "number" ? data.startedAt : null;
  const elapsedAtPause = Math.max(
    0,
    parseFloat(String(data.elapsedAtPause ?? 0)) || 0,
  );

  // Force re-render every second while running so remaining time stays live.
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!isRunning) return;
    const interval = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(interval);
  }, [isRunning]);

  const elapsed =
    isRunning && startedAt
      ? elapsedAtPause + (Date.now() - startedAt) / 1000
      : elapsedAtPause;
  const remaining = Math.max(0, totalSecs - elapsed);
  const isFinished = remaining <= 0 && (isRunning || elapsedAtPause > 0);

  const mins = Math.floor(remaining / 60);
  const secs = Math.floor(remaining % 60);
  const minStr = String(mins).padStart(2, "0");
  const secStr = String(secs).padStart(2, "0");
  const progress = totalSecs > 0 ? Math.max(0, remaining / totalSecs) : 1;
  const barColor =
    progress > 0.5 ? "#22c55e" : progress > 0.2 ? "#f59e0b" : "#ef4444";

  // Blinking colon while running
  const [colonVisible, setColonVisible] = useState(true);
  useEffect(() => {
    if (!isRunning) {
      setColonVisible(true);
      return;
    }
    const interval = setInterval(() => setColonVisible((v) => !v), 500);
    return () => clearInterval(interval);
  }, [isRunning]);

  // ── Inline time editing ──────────────────────────────────────────────────
  const [isEditing, setIsEditing] = useState(false);
  const [editMins, setEditMins] = useState("");
  const [editSecs, setEditSecs] = useState("");
  const minsRef = useRef<HTMLInputElement>(null);
  const secsRef = useRef<HTMLInputElement>(null);

  const startEditing = useCallback(() => {
    if (isRunning || !canEdit) return;
    setEditMins(minStr);
    setEditSecs(secStr);
    setIsEditing(true);
  }, [isRunning, canEdit, minStr, secStr]);

  useEffect(() => {
    if (isEditing) minsRef.current?.select();
  }, [isEditing]);

  const commitEdit = useCallback(() => {
    setIsEditing(false);
    const m = Math.max(
      0,
      Math.min(99, parseInt(minsRef.current?.value ?? editMins) || 0),
    );
    const s = Math.max(
      0,
      Math.min(59, parseInt(secsRef.current?.value ?? editSecs) || 0),
    );
    canvas.updateNodeData(id, {
      totalSeconds: Math.max(10, m * 60 + s),
      elapsedAtPause: 0,
    });
  }, [editMins, editSecs, id, canvas]);

  // ── Timer actions ────────────────────────────────────────────────────────
  const handlePlay = useCallback(() => {
    if (isRunning) {
      const addl = startedAt ? (Date.now() - startedAt) / 1000 : 0;
      canvas.updateNodeData(id, {
        isRunning: false,
        startedAt: null,
        elapsedAtPause: Math.min(totalSecs, elapsedAtPause + addl),
      });
    } else {
      if (elapsedAtPause >= totalSecs) return;
      canvas.updateNodeData(id, { isRunning: true, startedAt: Date.now() });
    }
  }, [isRunning, startedAt, elapsedAtPause, totalSecs, id, canvas]);

  const handleStop = useCallback(() => {
    canvas.updateNodeData(id, {
      isRunning: false,
      startedAt: null,
      elapsedAtPause: 0,
      totalSeconds: 120,
    });
  }, [id, canvas]);

  const handleAddMin = useCallback(() => {
    const addl = isRunning && startedAt ? (Date.now() - startedAt) / 1000 : 0;
    canvas.updateNodeData(id, {
      totalSeconds: totalSecs + 60,
      ...(isRunning
        ? { startedAt: Date.now(), elapsedAtPause: elapsedAtPause + addl }
        : {}),
    });
  }, [isRunning, startedAt, elapsedAtPause, totalSecs, id, canvas]);

  // ── Beep when time runs out ───────────────────────────────────────────────
  const wasFinishedRef = useRef(false);
  useEffect(() => {
    if (isFinished && !wasFinishedRef.current) {
      wasFinishedRef.current = true;
      try {
        const ctx = new AudioContext();
        const beep = (
          freq: number,
          start: number,
          duration: number,
          gain: number,
        ) => {
          const osc = ctx.createOscillator();
          const vol = ctx.createGain();
          osc.connect(vol);
          vol.connect(ctx.destination);
          osc.frequency.value = freq;
          osc.type = "sine";
          vol.gain.setValueAtTime(gain, ctx.currentTime + start);
          vol.gain.exponentialRampToValueAtTime(
            0.001,
            ctx.currentTime + start + duration,
          );
          osc.start(ctx.currentTime + start);
          osc.stop(ctx.currentTime + start + duration);
        };
        // Three short ascending tones
        beep(880, 0.0, 0.18, 0.4);
        beep(1100, 0.2, 0.18, 0.4);
        beep(1320, 0.4, 0.35, 0.5);
        setTimeout(() => ctx.close(), 1200);
      } catch {
        // AudioContext not available — silently skip
      }
    }
    if (!isFinished) wasFinishedRef.current = false;
  }, [isFinished]);

  // ── Styles ───────────────────────────────────────────────────────────────
  const digitColor = isFinished ? "#fca5a5" : "#f9faf5";

  const digitStyle: React.CSSProperties = {
    fontSize: 58,
    fontWeight: 700,
    fontFamily: "'Orbitron', 'Courier New', monospace",
    color: digitColor,
    lineHeight: "58px",
    fontVariantNumeric: "tabular-nums",
    display: "inline-block",
    width: 96,
    height: 58,
    textAlign: "center",
    verticalAlign: "top",
  };

  const inputStyle: React.CSSProperties = {
    fontSize: 58,
    fontWeight: 700,
    fontFamily: "'Orbitron', 'Courier New', monospace",
    color: digitColor,
    lineHeight: "58px",
    fontVariantNumeric: "tabular-nums",
    display: "inline-block",
    width: 96,
    height: 58,
    textAlign: "center",
    verticalAlign: "top",
    background: "rgba(255,255,255,.10)",
    border: "none",
    outline: "none",
    padding: 0,
    margin: 0,
    borderRadius: 6,
    boxSizing: "border-box",
    caretColor: "rgba(249,250,245,.65)",
    cursor: "text",
    WebkitAppearance: "none",
    MozAppearance: "none",
  };

  const btnBase: React.CSSProperties = {
    cursor: "pointer",
    pointerEvents: "all",
    border: "none",
  };

  return (
    <>
      {/* Load Orbitron once per document */}
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Orbitron:wght@700&display=swap');`}</style>

      {/* Outer card — no nodrag here so the node remains draggable */}
      <div
        style={{
          width: 300,
          background: "#ffffff",
          borderRadius: 20,
          overflow: "hidden",
          border: "1px solid rgba(0,0,0,.08)",
          boxShadow: "0 1px 3px rgba(0,0,0,.04),0 4px 16px rgba(0,0,0,.06)",
          fontFamily: "-apple-system,BlinkMacSystemFont,sans-serif",
          opacity: dragging ? 0.8 : 1,
          userSelect: "none",
        }}
      >
        {/* ── Display ──────────────────────────────────────────── */}
        <div
          style={{
            background: isFinished ? "#450a0a" : "#111827",
            padding: "26px 24px 20px",
            textAlign: "center",
            position: "relative",
            height: 126,
            boxSizing: "border-box",
          }}
        >
          {/* nodrag only on the clickable digit area — not the outer display */}
          <div
            className="nodrag"
            onClick={startEditing}
            onDoubleClick={(e) => e.stopPropagation()}
            style={{
              display: "inline-flex",
              alignItems: "center",
              cursor: isRunning || !canEdit ? "default" : "text",
            }}
          >
            {/* Minutes */}
            {isEditing ? (
              <input
                ref={minsRef}
                value={editMins}
                onChange={(e) =>
                  setEditMins(e.target.value.replace(/\D/g, "").slice(0, 2))
                }
                onBlur={(e) => {
                  if (e.relatedTarget === secsRef.current) return;
                  commitEdit();
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") commitEdit();
                  if (e.key === "Escape") setIsEditing(false);
                  if (e.key === "Tab") {
                    e.preventDefault();
                    secsRef.current?.select();
                  }
                }}
                style={inputStyle}
              />
            ) : (
              <span style={digitStyle}>{minStr}</span>
            )}

            {/* Colon */}
            <span
              style={{
                display: "inline-block",
                width: 22,
                textAlign: "center",
                flexShrink: 0,
                fontSize: 58,
                fontWeight: 700,
                fontFamily: "'Orbitron', 'Courier New', monospace",
                color: digitColor,
                lineHeight: 1,
                opacity: isRunning ? (colonVisible ? 1 : 0.18) : 1,
                transition: "opacity .05s",
              }}
            >
              :
            </span>

            {/* Seconds */}
            {isEditing ? (
              <input
                ref={secsRef}
                value={editSecs}
                onChange={(e) =>
                  setEditSecs(e.target.value.replace(/\D/g, "").slice(0, 2))
                }
                onBlur={commitEdit}
                onKeyDown={(e) => {
                  if (e.key === "Enter") commitEdit();
                  if (e.key === "Escape") setIsEditing(false);
                }}
                style={inputStyle}
              />
            ) : (
              <span style={digitStyle}>{secStr}</span>
            )}
          </div>

          {/* Fixed-height status line — always reserves space so the display never reflows */}
          <div
            style={{
              marginTop: 8,
              height: 14,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {isFinished && (
              <span
                style={{
                  color: "#fca5a5",
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: ".18em",
                  textTransform: "uppercase",
                  opacity: 0.9,
                }}
              >
                Time&apos;s up
              </span>
            )}
          </div>

          {/* Progress bar */}
          <div
            style={{
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              height: 3,
              background: "rgba(255,255,255,.07)",
            }}
          >
            <div
              style={{
                height: "100%",
                width: `${Math.round(progress * 100)}%`,
                background: barColor,
                borderRadius: "0 1.5px 1.5px 0",
                transition: "width .8s linear, background .8s",
              }}
            />
          </div>
        </div>

        {/* ── Controls ─────────────────────────────────────────── */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "11px 16px 13px",
            background: "#f8f8f7",
            borderTop: "1px solid rgba(0,0,0,.05)",
          }}
        >
          {/* + 1 min */}
          <button
            onClick={handleAddMin}
            onPointerDown={(e) => e.stopPropagation()}
            style={{
              ...btnBase,
              display: "inline-flex",
              alignItems: "center",
              gap: 5,
              padding: "7px 14px",
              borderRadius: 100,
              background: "rgba(0,0,0,.05)",
              border: "1px solid rgba(0,0,0,.09)",
              fontSize: 12,
              fontWeight: 600,
              color: "rgba(0,0,0,.60)",
            }}
          >
            <svg
              width="11"
              height="11"
              viewBox="0 0 11 11"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
            >
              <line x1="5.5" y1="1" x2="5.5" y2="10" />
              <line x1="1" y1="5.5" x2="10" y2="5.5" />
            </svg>
            1 min
          </button>

          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {/* Stop */}
            <button
              onClick={handleStop}
              onPointerDown={(e) => e.stopPropagation()}
              style={{
                ...btnBase,
                width: 36,
                height: 36,
                borderRadius: "50%",
                background: "white",
                border: "1.5px solid rgba(0,0,0,.12)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <svg
                width="10"
                height="10"
                viewBox="0 0 10 10"
                fill="rgba(0,0,0,.45)"
              >
                <rect width="10" height="10" rx="2" />
              </svg>
            </button>

            {/* Play / Pause */}
            <button
              onClick={handlePlay}
              onPointerDown={(e) => e.stopPropagation()}
              style={{
                ...btnBase,
                width: 36,
                height: 36,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "white",
                background: isRunning ? "#22c55e" : "#111827",
                boxShadow: `0 2px 10px ${isRunning ? "rgba(34,197,94,.45)" : "rgba(0,0,0,.30)"}`,
              }}
            >
              {isRunning ? (
                <span
                  style={{
                    display: "inline-flex",
                    gap: 3,
                    alignItems: "center",
                  }}
                >
                  <span
                    style={{
                      width: 3,
                      height: 13,
                      background: "currentColor",
                      borderRadius: 1.5,
                      display: "inline-block",
                    }}
                  />
                  <span
                    style={{
                      width: 3,
                      height: 13,
                      background: "currentColor",
                      borderRadius: 1.5,
                      display: "inline-block",
                    }}
                  />
                </span>
              ) : (
                <svg
                  width="11"
                  height="14"
                  viewBox="0 0 11 14"
                  fill="currentColor"
                >
                  <path d="M0 0L11 7 0 14z" />
                </svg>
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
});
